// client/src/services/healthMonitoring.ts
// Real-time health monitoring for chunks and app performance

export interface ChunkMetrics {
  name: string;
  successCount: number;
  failureCount: number;
  totalAttempts: number;
  averageLoadTime: number;
  lastLoadTime: number;
  lastFailureTime?: number;
  lastFailureReason?: string;
  successRate: number; // 0-100
  status: 'healthy' | 'degraded' | 'critical';
}

export interface AppHealthStatus {
  version: string;
  uptime: number;
  errorRate: number;
  chunkHealthy: number;
  chunkDegraded: number;
  chunkCritical: number;
  shouldRollback: boolean;
  timestamp: number;
}

class HealthMonitor {
  private metrics = new Map<string, ChunkMetrics>();
  private appStartTime = Date.now();
  private errorThresholds = {
    degraded: 0.05, // 5% error rate = degraded
    critical: 0.10, // 10% error rate = critical
  };
  private initialized = false;

  initialize() {
    if (this.initialized) return;
    this.initialized = true;  }

  recordChunkLoad(chunkName: string, success: boolean, loadTime: number) {
    const existing = this.metrics.get(chunkName) || {
      name: chunkName,
      successCount: 0,
      failureCount: 0,
      totalAttempts: 0,
      averageLoadTime: 0,
      lastLoadTime: 0,
      successRate: 100,
      status: 'healthy' as const,
    };

    existing.totalAttempts++;
    existing.lastLoadTime = loadTime;

    if (success) {
      existing.successCount++;
      existing.averageLoadTime =
        (existing.averageLoadTime * (existing.totalAttempts - 1) + loadTime) /
        existing.totalAttempts;
    } else {
      existing.failureCount++;
      existing.lastFailureTime = Date.now();
    }

    existing.successRate = (existing.successCount / existing.totalAttempts) * 100;
    existing.status = this.calculateStatus(existing.successRate);

    this.metrics.set(chunkName, existing);

    // Send to analytics
    this.sendMetricsToAnalytics(chunkName, existing);
  }

  recordChunkError(chunkName: string, error: Error) {
    const existing = this.metrics.get(chunkName);
    if (existing) {
      existing.lastFailureReason = error.message;
      existing.lastFailureTime = Date.now();
    }
  }

  private calculateStatus(successRate: number): 'healthy' | 'degraded' | 'critical' {
    const errorRate = 1 - successRate / 100;
    if (errorRate >= this.errorThresholds.critical) return 'critical';
    if (errorRate >= this.errorThresholds.degraded) return 'degraded';
    return 'healthy';
  }

  getAppHealth(): AppHealthStatus {
    const chunks = Array.from(this.metrics.values());
    const totalErrors = chunks.reduce((sum, c) => sum + c.failureCount, 0);
    const totalAttempts = chunks.reduce((sum, c) => sum + c.totalAttempts, 0);

    return {
      version: import.meta.env.VITE_APP_VERSION || 'unknown',
      uptime: Date.now() - this.appStartTime,
      errorRate: totalAttempts > 0 ? totalErrors / totalAttempts : 0,
      chunkHealthy: chunks.filter(c => c.status === 'healthy').length,
      chunkDegraded: chunks.filter(c => c.status === 'degraded').length,
      chunkCritical: chunks.filter(c => c.status === 'critical').length,
      shouldRollback: chunks.filter(c => c.status === 'critical').length > 2,
      timestamp: Date.now(),
    };
  }

  getChunkMetrics(chunkName: string): ChunkMetrics | undefined {
    return this.metrics.get(chunkName);
  }

  getAllMetrics(): ChunkMetrics[] {
    return Array.from(this.metrics.values());
  }

  private sendMetricsToAnalytics(chunkName: string, metrics: ChunkMetrics) {
    // Send to backend analytics
    try {
      navigator.sendBeacon('/api/analytics/chunk-metrics', JSON.stringify({
        chunk: chunkName,
        successRate: metrics.successRate,
        averageLoadTime: metrics.averageLoadTime,
        status: metrics.status,
        timestamp: Date.now(),
      }));
    } catch (error) {
    }
  }
}

export const healthMonitor = new HealthMonitor();

